<script setup>
import { RouterView, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from './stores/auth'
import { useI18n } from 'vue-i18n'
import { ref, computed, watch } from 'vue'
import AuthMonitor from '@/components/AuthMonitor.vue'
import AppUpdateDialog from '@/components/AppUpdateDialog.vue'
import { useVersionCheck } from '@/composables/useVersionCheck'
import api from '@/api/api'
import { i18n } from '@/i18n/plugin'

const { t } = useI18n({
  messages: {
    fr: {
      logout: 'Se déconnecter',
      myprofile: 'Mon profil',
      impersonating: 'Connecté en tant que',
      exitImpersonation: 'Retour au compte root',
      menu: {
        subscriptions: 'Mes inscriptions',
        events: 'Mes événements',
        myTeams: 'Mes équipes',
        rootAdmin: 'Console Root Admin',
        myProfile: 'Mon profil'
      }
    },
    en: {
      myprofile: 'My profile',
      logout: 'Sign out',
      impersonating: 'Viewing as',
      exitImpersonation: 'Return to root account',
      menu: {
        subscriptions: 'My subscriptions',
        events: 'My events',
        myTeams: 'My teams',
        rootAdmin: 'Root Admin Console',
        myProfile: 'My profile'
      }
    }
  }
})

const authStore = useAuthStore()

const router = useRouter()

// Computed property for team leader status from account
const isLeader = computed(() => authStore.account?.isLeader === true)

const logout = () => {
  authStore.logOut()
  router.push('/login')
}

const exitImpersonation = async () => {
  try {
    await authStore.exitImpersonation()
    router.push('/root-admin')
  } catch (e) {
    console.error('Error exiting impersonation:', e)
  }
}

// Language selection
const selectedLang = ref('fr')

// Initialize from account when available
watch(
  () => authStore.account,
  (acc) => {
    if (acc?.lang) {
      selectedLang.value = acc.lang
      i18n.global.locale.value = acc.lang
    }
  },
  { immediate: true }
)

// Update account and locale when language changes
const changeLanguage = async (newLang) => {
  if (!authStore.account) return

  selectedLang.value = newLang
  i18n.global.locale.value = newLang

  try {
    await api.updateAccount({
      uid: authStore.account.uid,
      lang: newLang
    })

    // Update local store
    authStore.account = {
      ...authStore.account,
      lang: newLang
    }
  } catch (error) {
    console.error('Failed to update language:', error)
  }
}

// Toggle between languages
const toggleLanguage = () => {
  const newLang = selectedLang.value === 'fr' ? 'en' : 'fr'
  changeLanguage(newLang)
}

// Get display text for current language
const currentLangDisplay = computed(() => {
  return selectedLang.value === 'fr' ? 'Français' : 'English'
})

const menuItems = [
  { name: 'events', path: '/events', admin: true, root: false, leader: false },
  { name: 'subscriptions', path: '/subscriptions', admin: false, root: false, leader: false },
  { name: 'myTeams', path: '/teams/my-teams', admin: false, root: false, leader: true },
  { name: 'rootAdmin', path: '/root-admin', admin: false, root: true, leader: false },
  { name: 'myProfile', path: '/profile', admin: false, root: false, leader: false }
]

// Avoid accessing account before it's hydrated
const filteredMenuItems = computed(() =>
  menuItems.filter((m) => {
    // Root admin items only for root users
    if (m.root) {
      return authStore.account && authStore.account.isRoot
    }
    // Leader-only items
    if (m.leader) {
      return isLeader.value
    }
    // Regular admin items for admins
    return (authStore.account && authStore.account.isAdmin) || !m.admin
  })
)

const currentTab = ref('subscriptions')

// Mobile drawer state
const drawer = ref(false)

const { isLoggedIn } = storeToRefs(authStore)

// Initialize version checking
useVersionCheck()

router.beforeEach((to) => {
  if (to.path.indexOf('/subscriptions') == 0) {
    currentTab.value = 'subscriptions'
  } else {
    currentTab.value = 'events'
  }
  return true
})
</script>

<template>
  <!-- Global auth monitoring for proactive token management across the entire app -->
  <AuthMonitor v-if="isLoggedIn" :check-interval-minutes="5" :refresh-threshold-minutes="10" />

  <!-- Global app update notification (handles both chunk errors and version updates) -->
  <AppUpdateDialog />

  <v-layout class="rounded rounded-md h-100">
    <!-- Mobile navigation drawer -->
    <v-navigation-drawer v-if="isLoggedIn" v-model="drawer" temporary class="hidden-md-and-up">
      <v-list>
        <v-list-item
          v-for="menu in filteredMenuItems"
          :key="menu.name"
          :to="menu.path"
          @click="drawer = false"
        >
          <v-list-item-title>{{ t(`menu.${menu.name}`) }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar v-if="isLoggedIn">
      <v-app-bar-title class="flex-0-0 mx-2">
        <img class="app-bar-logo" src="/images/logo-adv-v2.png" alt="PlanToRide" />
      </v-app-bar-title>

      <!-- Hamburger menu button (mobile only) -->
      <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" />

      <!-- Desktop/tablet tabs -->
      <v-tabs v-model="currentTab" class="hidden-sm-and-down" background-color="transparent" grow>
        <v-tab
          v-for="menu in filteredMenuItems"
          v-bind:key="menu.name"
          :value="menu.name"
          :to="menu.path"
        >
          {{ t(`menu.${menu.name}`) }}
        </v-tab>
      </v-tabs>
      <v-spacer />

      <!-- Impersonation indicator -->
      <v-chip
        v-if="authStore.isImpersonating"
        color="warning"
        prepend-icon="mdi-account-switch"
        class="mr-2"
      >
        <span class="hidden-xs">{{ t('impersonating') }}: {{ authStore.account?.email }}</span>
        <span class="hidden-sm-and-up">{{ t('impersonating') }}</span>
        <template v-slot:append>
          <v-btn
            icon="mdi-close"
            size="x-small"
            variant="text"
            @click="exitImpersonation"
            :title="t('exitImpersonation')"
          />
        </template>
      </v-chip>

      <!-- Language selector -->
      <v-btn @click="toggleLanguage" prepend-icon="mdi-translate" variant="text" class="mr-2">
        {{ currentLangDisplay }}
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon="mdi-account-circle" v-bind="props" />
        </template>
        <v-list>
          <v-list-item v-if="authStore.user">
            <v-list-item-title>{{ authStore.user.displayName }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ authStore.user.email }}
              <v-divider class="my-2"></v-divider>
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item to="/profile" prepend-icon="mdi-account-edit" @click="currentTab = ''">
            <v-list-item-title>{{ t('myprofile') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="logout" prepend-icon="mdi-logout">
            <v-list-item-title>{{ t('logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main class="h-100 overflow-y-auto">
      <RouterView />
    </v-main>
  </v-layout>
</template>

<style scoped>
.app-bar-logo {
  max-height: 48px;
  height: auto;
  width: auto;
  vertical-align: middle;
}
</style>
